//
// forms/validation.js
//
// Create a 'Validation Data Collection' like the following:
//   [
//     [<element>, <validation_object>],
//     ...
//   ].all_ok = <bool>
//
//   ('element' is the parent div which contains all elements making up the form field.)
//
// The <validation object> contains details about the result of validation:
//   {
//     ok: <bool>,
//     reason: <REASON>,  // if not ok, the reason why invalid -- see REASONS
//   }

import * as value_extractor from './value-extractor';

const REASON = {
  Required      : 'required',
  InvalidFormat : 'invalid format',
  OutOfRange    : 'invalid range',
  NotBusinessy  : 'non-business',
  NotFullName   : 'not full name',
};

function get_validation_data_collection(form_elements) {
  // Get 'Form Values Object' from value_extractor
  const form_values_object = value_extractor.get_form_data(form_elements);

  // Convert to a Validation Data Collection
  const vdc = Object.keys(form_values_object).reduce(function(vdc, k) {
    const item = form_values_object[k];
    item.type = item.div.getAttribute('data-type');

    const validation_obj = validate(item);
    vdc.all_ok = vdc.all_ok === undefined ? validation_obj.ok : vdc.all_ok && validation_obj.ok;
    vdc.push([item.div, validation_obj]);

    return vdc;
  }, [ ]);

  return vdc;
}


// Validators
// ----------------------------------
// - NB div is passed to make all item options available
//

function validate(item) {
  // Send to appropriate validator for type
  if (!validators.hasOwnProperty(item.type)) {
    throw Error('validate(): no validator for type "' + item.type + '"');
  }
  const required = item.div.getAttribute('data-required') === 'true' && !item.div.classList.contains('c-form-set--hidden');
  return validators[item.type](item.div, item.value, required);
}

// Some Validation Object helper factories
function mk_validation_obj__success() {
  return { ok: true };
}
function mk_validation_obj__failure(reason) {
  return {
    ok: false,
    reason: reason,
  };
}

const validators = {
  'hidden': function(item_div, value, required) {
    return mk_validation_obj__success();
  },

  text: function(item_div, value, required) {
    return (required && value === '') ? mk_validation_obj__failure(REASON.Required) : mk_validation_obj__success();
  },

  textarea: function(item_div, value, required) {
    return (required && value === '') ? mk_validation_obj__failure(REASON.Required) : mk_validation_obj__success();
  },

  // pattern-based
  pattern_match: function(item_div, value, required, regex) {
    if (value === '') {
      return required ? mk_validation_obj__failure(REASON.Required) : mk_validation_obj__success();
    }
    return value.match(regex) ? mk_validation_obj__success() : mk_validation_obj__failure(REASON.InvalidFormat);
  },

  tel: function(...args) {
    return validators.pattern_match(...args, /^[0-9 \-\+\(\)]{5,}$/);
  },

  email: function(item_div, value, required) {
    const result = validators.pattern_match(item_div, value, required, email_regex());
    const el_input = item_div.getElementsByClassName('js-input')[0];
    const should_check_business = el_input.hasAttribute('data-business-filter');

    if (result.ok && value !== '' && should_check_business) {
      const domain = value.split('@')[1].toLowerCase();

      const not_filtered_wildcard = !filtered_email_wildcards().some((wildcard) => domain.includes(wildcard));
      const not_filtered_domain =  !filtered_email_domains().includes(domain);

      result.ok = not_filtered_wildcard && not_filtered_domain;
      if (!result.ok) {
        result.reason = REASON.NotBusinessy;
      }
    }
    return result;
  },

  url: function(...args) {
    return validators.pattern_match(...args, url_regex());
  },

  textnumber: function(item_div, value, required) {
    // textnumber: validate numbers (text field used due to inability to differentiate
    // between empty & invalid -- number field returns '' for both)
    if (value === '') {
      return required ? mk_validation_obj__failure(REASON.Required) : mk_validation_obj__success();
    }

    const is_numeric = value.match(/^-?[0-9]+(\.[0-9]+)?$/);
    if (!is_numeric) {
      return mk_validation_obj__failure(REASON.InvalidFormat);
    }

    const n = parseFloat(value);
    const input = item_div.getElementsByClassName('js-input')[0];
    const min = parseFloat(input.getAttribute('min'));
    const max = parseFloat(input.getAttribute('max'));

    let is_ok = true;
    if (min && max) { is_ok = (n >= min && n <= max); }
    else if (min)   { is_ok = (n >= min);             }
    else if (max)   { is_ok = (n <= max);             }

    if (!is_ok) {
      return mk_validation_obj__failure(REASON.OutOfRange);
    }
    return mk_validation_obj__success();
  },

  date: function(item_div, value, required) {
    // Not 100% clear what to do here yet
    return mk_validation_obj__success();
  },

  select: function(item_div, value, required) {
    return (required && value === '') ? mk_validation_obj__failure(REASON.Required) : mk_validation_obj__success();
  },

  checkbox: function(item_div, value, required) {
    return (required && value === '') ? mk_validation_obj__failure(REASON.Required) : mk_validation_obj__success();
  },

  checkboxes: function(item_div, value, required) {
    return mk_validation_obj__success(); // 'required' makes little sense for multi-select checkboxes
  },

  radios: function(item_div, value, required) {
    return (required && value === null) ? mk_validation_obj__failure(REASON.Required) : mk_validation_obj__success();
  },

  slider: function(item_div, value, required) {
    return mk_validation_obj__success(); // A slider always has a value
  },
};


function email_regex() {
  // This is apparently valid for RFC 5322
  if (email_regex.hasOwnProperty('regex')) {
    return email_regex.regex;
  }
  return email_regex.regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
}

function url_regex() {
  // This is by Diego Operini and is the most accurate I could find
  // - https://mathiasbynens.be/demo/url-regex
  // - https://gist.github.com/dperini/729294
  if (url_regex.hasOwnProperty('regex')) {
    return url_regex.regex;
  }
  return url_regex.regex = /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i;
}

function filtered_email_wildcards() {
  return [
    'gmail',
    'hotmail',
    'yahoo',
    'outlook',
  ];
}

function filtered_email_domains() {
  if (filtered_email_domains.hasOwnProperty('domains')) {
    return filtered_email_domains.domains;
  }
  return filtered_email_domains.domains = [
    // Competitors :/
    'sprinklr.com', 'netbase.com', 'synthesio.com', 'sysomos.com', 'talkwalker.com',
    'linkfluence.com', 'pulsarplatform.com', 'digimind.com', 'nuvi.com', 'tracx.com',

    // Non-business emails
    'aol.com', 'att.net', 'comcast.net', 'gmail.com', 'googlemail.com',
    'hotmail.com', 'hotmail.co.uk', 'mac.com', 'me.com', 'mail.com', 'msn.com',
    'live.com', 'sbcglobal.net', 'verizon.net', 'yahoo.com', 'yahoo.co.uk',

    'email.com', 'games.com' /* AOL */, 'hush.com', 'hushmail.com', 'icloud.com', 'inbox.com',
    'lavabit.com', 'love.com' /* AOL */, 'outlook.com', 'pobox.com', 'rocketmail.com' /* Yahoo */,
    'safe-mail.net', 'wow.com' /* AOL */, 'ygm.com' /* AOL */, 'ymail.com' /* Yahoo */, 'zoho.com',
    'fastmail.fm', 'yandex.com', 'protonmail.com', 'myself.com', 'post.com', 'writeme.com',

    // USA
    'bellsouth.net', 'charter.net', 'comcast.net', 'cox.net', 'earthlink.net', 'juno.com',

    // Britain
    'btinternet.com', 'virginmedia.com', 'blueyonder.co.uk', 'freeserve.co.uk', 'live.co.uk',
    'ntlworld.com', 'o2.co.uk', 'orange.net', 'tiscali.co.uk',
    'virgin.net', 'wanadoo.co.uk',

    // Asia
    'sina.com', 'qq.com', 'naver.com', 'hanmail.net', 'daum.net', 'nate.com', 'yahoo.co.jp', 'yahoo.co.kr',
    'yahoo.co.id', 'yahoo.co.in', 'yahoo.com.sg', 'yahoo.com.ph',

    // France
    'hotmail.fr', 'live.fr', 'laposte.net', 'yahoo.fr', 'wanadoo.fr', 'orange.fr', 'gmx.fr', 'sfr.fr',
    'neuf.fr', 'free.fr',

    // Germany
    'gmx.de', 'gmx.com', 'gmx.net', 'gmx.at', 'gmx.ch', 'hotmail.de', 'live.de', 'online.de', 't-online.de', 'web.de', 'yahoo.de',
    'aol.de', 'freenet.de', 'outlook.de', 'writeme.de',

    // Russia
    'mail.ru', 'rambler.ru', 'yandex.ru', 'ya.ru', 'list.ru',

    // Belgium
    'hotmail.be', 'live.be', 'skynet.be', 'voo.be', 'tvcablenet.be', 'telenet.be',

    // Argentina
    'hotmail.com.ar', 'live.com.ar', 'yahoo.com.ar', 'fibertel.com.ar', 'speedy.com.ar', 'arnet.com.ar',

    // Mexico
    'hotmail.com', 'gmail.com', 'yahoo.com.mx', 'live.com.mx', 'yahoo.com', 'hotmail.es', 'live.com',
    'hotmail.com.mx', 'prodigy.net.mx', 'msn.com',

    // Brazil
    'yahoo.com.br', 'hotmail.com.br', 'outlook.com.br', 'uol.com.br', 'bol.com.br', 'terra.com.br',
    'ig.com.br', 'itelefonica.com.br', 'r7.com', 'zipmail.com.br', 'globo.com', 'globomail.com', 'oi.com.br',
  ];
}

export {
  get_validation_data_collection,
  REASON,
};

